import { useNavigate } from 'react-router-dom'
import { ROLE, ROLE_T, TOKEN } from 'src/static/const'
import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { MainContext } from 'src/context'
import { Spin } from 'antd'
import { useTranslation } from 'src/i18n/context'
import { useUserStore } from 'src/store/userStore'
// import { GetStatisticsUniversityGenderConfig } from 'src/service/api'

type MousePos = { x: number; y: number }

const useInView = (threshold = 0.15) => {
	const ref = useRef<HTMLDivElement>(null)
	const [isInView, setIsInView] = useState(false)

	useEffect(() => {
		const el = ref.current
		if (!el) return

		const obs = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) setIsInView(true)
			},
			{ threshold },
		)

		obs.observe(el)
		return () => obs.disconnect()
	}, [threshold])

	return [ref, isInView] as const
}

const AnimatedCounter = ({
	end,
	duration = 1800,
	suffix = '',
	decimals = 0,
}: {
	end: number
	duration?: number
	suffix?: string
	decimals?: number
}) => {
	const [count, setCount] = useState<string>('0')
	const [ref, inView] = useInView()

	useEffect(() => {
		if (!inView) return
		let startTime: number | null = null

		const step = (ts: number) => {
			if (startTime === null) startTime = ts
			const progress = Math.min((ts - startTime) / duration, 1)
			const rawValue = (progress * end).toFixed(decimals)
			setCount(decimals === 0 ? Number(rawValue).toLocaleString() : rawValue)
			if (progress < 1) requestAnimationFrame(step)
		}

		requestAnimationFrame(step)
	}, [inView, end, duration, decimals])

	return (
		<span ref={ref}>
			{count}
			{suffix}
		</span>
	)
}

function HomePage() {
	const navigate = useNavigate()
	const { setLoginOpen } = useContext(MainContext)
	const { userInfo } = useUserStore()
	const { t } = useTranslation()
	const [play, setPlay] = useState(false)
	const [mouse, setMouse] = useState<MousePos>({ x: 0, y: 0 })
	// statistics/university-gender returns 404, static values used until API is fixed
	const participantStats = { teachers: 38400, students: 11763 }
	// const [participantStats, setParticipantStats] = useState<{
	// 	teachers: number;
	// 	students: number;
	// }>({ teachers: 0, students: 0 })
	// const [loadingParticipantStats, setLoadingParticipantStats] = useState(false)

	const isUser = (() => {
		if (userInfo) return true
		const role = localStorage.getItem(ROLE)
		const token = localStorage.getItem(TOKEN)
		return role === ROLE_T && !!token
	})()

	// Fetch participant statistics
	// useEffect(() => {
	// 	const fetchStats = async () => {
	// 		setLoadingParticipantStats(true);
	// 		try {
	// 			// Fetch teacher survey (quizRuleId=3) statistics
	// 			const teacherRes = await GetStatisticsUniversityGenderConfig({ quizRuleId: 3 });
	// 			// Fetch student survey (quizRuleId=4) statistics
	// 			const studentRes = await GetStatisticsUniversityGenderConfig({ quizRuleId: 4 });

	// 			let teacherTotal = 0;
	// 			let studentTotal = 0;

	// 			if (teacherRes?.data && Array.isArray(teacherRes.data)) {
	// 				teacherTotal = teacherRes.data.reduce((sum: number, item: any) => sum + (item.participantTotal || 0), 0);
	// 			}

	// 			if (studentRes?.data && Array.isArray(studentRes.data)) {
	// 				studentTotal = studentRes.data.reduce((sum: number, item: any) => sum + (item.participantTotal || 0), 0);
	// 			}

	// 			setParticipantStats({
	// 				teachers: teacherTotal,
	// 				students: studentTotal,
	// 			});
	// 		} catch (error) {
	// 			console.error('Failed to fetch participant stats:', error);
	// 		} finally {
	// 			setLoadingParticipantStats(false);
	// 		}
	// 	};

	// 	fetchStats();
	// }, [])

	const particles = useMemo(
		() =>
			Array.from({ length: 16 }).map((_, i) => ({
				id: i,
				left: Math.random() * 100,
				top: Math.random() * 100,
				delay: Math.random() * 4,
				duration: 6 + Math.random() * 6,
				opacity: 0.2 + Math.random() * 0.25,
			})),
		[],
	)

	useEffect(() => {
		const onMove = (e: MouseEvent) => setMouse({ x: e.clientX, y: e.clientY })
		window.addEventListener('mousemove', onMove)
		return () => window.removeEventListener('mousemove', onMove)
	}, [])

	const handleCheck = () => {
		setLoginOpen?.(true)
		// message.warning(t("home.loginRequired"))
	}

	return (
		<div className='home'>
			<div className='home__intro'>
				<div className='home__intro-info container'>
					<div className='home__intro-left'>
						{/* Title */}
						<h2 className='title home__title-animate home__hero-title'>
							{t("home.title")}
						</h2>

						{/* Info */}
						<p className='info home__info-animate'>
							{t("home.description")}
						</p>


						{/* Apply button */}
						<div className='home__cta home__button-animate'>

							<div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
								{/* <button
									onClick={() => window.open('https://my.hemis.uz', '_blank')}
									className='arrowBtn'
									style={{ width: 'auto' }}
								>
									<span>{t("auth.student")}</span>
								</button> */}
								<button
									onClick={handleCheck}
									className='arrowBtn'
									style={{ width: 'auto' }}
								>
									<span>{t("auth.teacher")}</span>
								</button>
							</div>

						</div>

						<div className='home__stats'>
							<div className='stat'>
								<div className='value'>
									<AnimatedCounter end={48} suffix='K+' />
								</div>
								<div className='label'>{t('home.stats.users')}</div>
							</div>
							{/* <div className='divider' />
							<div className='stat'>
								<div className='value'>
									<AnimatedCounter end={1.6} decimals={1} suffix='M+' />
								</div>
								<div className='label'>{t('home.stats.students')}</div>
							</div> */}

							<div className='divider' />
							<div className='stat'>
								<div className='value'>
									<AnimatedCounter end={200} suffix='+' />
								</div>
								<div className='label'>{t('home.stats.universities')}</div>
							</div>


						</div>

						{/* Participant statistics */}
						<Spin spinning={false}>
							<div className='home__stats home__participant-stats'>
								<div className='stat-title'>{t('home.stats.surveyParticipants')}</div>
								<div className='stat-row'>
									<div className='stat'>
										<div className='value'>
											<AnimatedCounter end={participantStats.teachers} />
										</div>
										<div className='label'>{t('home.stats.teachers')}</div>
									</div>
									{/* <div className='divider' />
									<div className='stat'>
										<div className='value'>
											<AnimatedCounter end={participantStats.students} />
										</div>
										<div className='label'>{t('home.stats.studentsParticipated')}</div>
									</div> */}
									<div className='divider' />
									<div className='stat'>
										<div className='value'>
											<AnimatedCounter end={participantStats.teachers + participantStats.students} />
										</div>
										<div className='label'>{t('home.stats.total')}</div>
									</div>
								</div>
							</div>
						</Spin>
						{/* Anonymous info */}
						<div className='home__anonymous'>
							<span className='icon'>🔒</span>
							<span>{t("home.anonymous")}</span>
						</div>
					</div>

					
					<div className='home__intro-right'>
						{/* Background blobs (parallax) */}
						<div
							className='home__blob home__blob-1'
							style={{
								transform: `translate(${mouse.x * 0.01}px, ${mouse.y * 0.01}px)`,
							}}
						/>
						<div
							className='home__blob home__blob-2'
							style={{
								transform: `translate(${mouse.x * -0.008}px, ${mouse.y * -0.01}px)`,
							}}
						/>
						<div className='home__blob home__blob-3' />

						{/* Floating icons */}
						<div className='home__float home__float-1'>📊</div>
						<div className='home__float home__float-2'>🧠</div>
						<div className='home__float home__float-3'>📝</div>

						{/* Particles */}
						<div className='home__particles' aria-hidden='true'>
							{particles.map((p) => (
								<span
									key={p.id}
									className='home__particle'
									style={{
										left: `${p.left}%`,
										top: `${p.top}%`,
										animationDelay: `${p.delay}s`,
										animationDuration: `${p.duration}s`,
										opacity: p.opacity,
									}}
								/>
							))}
						</div>

						<img
							src={require('../../assets/images/home-image.png')}
							alt='author'
							className='intro1 home__image-animate'
							draggable={false}
						/>
					</div>


				</div>
			</div>

			{/* Remaining page content */}
			<div className='home__content'>
				<div className='container'>
					<div className='home__content-grid'>
						<div className='home__card'>
							<div className='home__card-icon'>📝</div>
							<h3>{t('home.cards.takeQuizTitle')}</h3>
							<p>{t('home.cards.takeQuizDesc')}</p>
						</div>
						<div className='home__card'>
							<div className='home__card-icon'>📊</div>
							<h3>{t('home.cards.resultsTitle')}</h3>
							<p>{t('home.cards.resultsDesc')}</p>
						</div>
						<div className='home__card'>
							<div className='home__card-icon'>💬</div>
							<h3>{t('home.cards.supportTitle')}</h3>
							<p>{t('home.cards.supportDesc')}</p>
						</div>
					</div>
				</div>
			</div>
			{/* 
      <div className="home__video container">
        <div className="home__video-info">
          <h2 className="title">Tizimidan qanday foydalaniladi?</h2>
          <p className="info">
            Tizimga Hemis yoki One Id orqali kiring va tegishli mavzudagi test
            va so'rovnomalarni bajaring.
          </p>
        </div>
        <div className="video">
          {play ? (
            <iframe
              width="520"
              height="320"
              src="https://www.youtube.com/embed/ym5AjtIeI_E?controls=0&autoplay=1"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen={true}
              title="usage-video"
            ></iframe>
          ) : (
            <div className="video__frame">
              <img src={require("src/assets/images/video.png")} alt="video" />
              <div className="play">
                <svg
                  onClick={() => setPlay(true)}
                  width="117"
                  height="117"
                  viewBox="0 0 117 117"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle cx="58.5" cy="58.5" r="58.5" fill="white" />
                  <path
                    d="M53.5294 44.6394C51.028 43.1397 49 44.3683 49 47.3814V69.6165C49 72.6325 51.028 73.8595 53.5294 72.3612L72.1233 61.2156C74.6256 59.7154 74.6256 57.2847 72.1233 55.7849L53.5294 44.6394Z"
                    fill="#1890ff"
                  />
                </svg>
              </div>
            </div>
          )}
        </div>
      </div> */}
		</div>
	)
}

export default HomePage
